import React, { useMemo, useState, useEffect } from 'react'
import { cn, getUserType } from '@utils/safeTs'
import styles from './EditorOrdineAzioni.module.sass'
import { Alert, Button, Flag, FormField, FormTextarea } from '@components/atoms'
import { ArrowRight } from '@components/icons'
import useTrans from '@hooks/useTrans'
import { useRouter } from 'next/router'
import { api } from '@utils/index'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { useEditorContext } from 'context/EditorContext'
import { dataURLtoBlob } from '@utils/download'
import { EDITOR_SCOPE_ORDER, getEditorUrl } from '@utils/editor-bozze'
import { EditorErrorType, EditorStatusType, RootStateType, UserType } from '@utils/types'
import { useSelector } from 'react-redux'
import {
  getIsEditable as getIsCaricamentoBozzePosizioneEditable,
  getNextPosizioneEditabile as getNextPosizioneCaricamentoBozzeEditabile,
} from '@components/organisms/FlussoPersonalizzazioneCaricamentoBozzePosizione/FlussoPersonalizzazioneCaricamentoBozzePosizione'

interface Props {
  className?: string
  personalizzazioneId: number | string | null
  selectedPosizioneOrdineId?: number | string | null
  ordineId?: number | string | null
  ordineNumeroProtocollo?: string | null
  ordineData?: any
  editorState?: EditorStatusType
  initialNote?: string
  errors?: EditorErrorType[]
}

const getIsApprovazioneBozzeEditable = (pos: any, userType: UserType | undefined) => {
  if (!pos) return false

  return (
    pos.statoPosizionePersonalizzazione === 'BOZZA_INVIATA' &&
    ['admin', 'cliente'].includes(userType as UserType)
  )
}

const getNextPosizioneApprovazioneBozzeEditabile = (
  personalizzazioni: any[],
  posizione: any,
  userType: UserType | undefined,
  fromTop = false
) => {
  if (!posizione) return null

  let found = fromTop

  for (const personalizzazione of personalizzazioni || []) {
    for (const pos of personalizzazione?.posizioniPersonalizzazioneOrdine || []) {
      if (fromTop && String(pos?.id) === String(posizione?.id)) {
        return null
      }

      if (found && getIsApprovazioneBozzeEditable(pos, userType)) {
        return {
          posizioneId: pos?.id,
          personalizzazioneId: personalizzazione?.id,
          stato: pos?.statoPosizionePersonalizzazione,
        }
      }

      if (String(pos?.id) === String(posizione?.id)) {
        found = true
      }
    }
  }

  if (!fromTop) {
    return getNextPosizioneApprovazioneBozzeEditabile(personalizzazioni, posizione, userType, true)
  }

  return null
}

const EditorOrdineAzioni = (props: Props) => {
  const {
    className = '',
    personalizzazioneId,
    selectedPosizioneOrdineId = null,
    ordineId = null,
    ordineNumeroProtocollo = null,
    ordineData = null,
    editorState = null,
    initialNote = '',
    errors = [],
  } = props

  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)
  const { canvases, getActions } = useEditorContext()

  const [note, setNote] = useState(initialNote || '')
  const [savedNote, setSavedNote] = useState(initialNote || '')
  const [isLoading, setLoading] = useState(false)

  const hasDangerErrors = useMemo(
    () => (errors || []).some((error) => error.variant === 'danger'),
    [errors]
  )
  const canvasIds = useMemo(() => Object.keys(canvases || {}), [canvases])

  const isValid = useMemo(() => {
    if (hasDangerErrors) return false
    if (!canvasIds.length) return false
    return canvasIds.every((cId: string) => canvases[cId]?.data?.length > 0)
  }, [canvasIds, canvases, hasDangerErrors])

  const isEditorCanvasDirty = useMemo(() => {
    if (!canvasIds.length) return false
    return canvasIds.some((cId: string) => !!canvases[cId]?.isDirty)
  }, [canvasIds, canvases])

  const isDirty = useMemo(() => {
    const noteChanged = (note || '').trim() !== (savedNote || '').trim()
    return noteChanged || isEditorCanvasDirty
  }, [isEditorCanvasDirty, note, savedNote])

  const editorAlert = useMemo(() => {
    if (!hasDangerErrors) return undefined
    return {
      variant: 'danger' as const,
      message: t('Per procedere, devi prima correggere gli errori indicati'),
    }
  }, [hasDangerErrors, t])

  const currentOrdineData = useMemo(() => {
    if (!ordineData) return null
    if (ordineData?.personalizzazioniOrdine?.length || !ordineData?.ordini?.length)
      return ordineData

    const matchingOrdine = ordineData.ordini.find(
      (ordine: any) => ordine?.numeroProtocollo === ordineNumeroProtocollo
    )

    return matchingOrdine || ordineData.ordini[0] || null
  }, [ordineData, ordineNumeroProtocollo])

  const currentPersonalizzazione = useMemo(() => {
    const personalizzazioni = currentOrdineData?.personalizzazioniOrdine || []
    return personalizzazioni.find((p: any) => String(p?.id) === String(personalizzazioneId)) || null
  }, [currentOrdineData, personalizzazioneId])

  const currentPosizioneOrdine = useMemo(() => {
    if (!selectedPosizioneOrdineId) return null

    return (
      currentPersonalizzazione?.posizioniPersonalizzazioneOrdine?.find(
        (pos: any) => String(pos?.id) === String(selectedPosizioneOrdineId)
      ) || null
    )
  }, [currentPersonalizzazione, selectedPosizioneOrdineId])

  const persistPosizioni = async () => {
    const requests =
      editorState?.posizioni?.map(async (pos: any) => {
        const canvasId = pos?.id
        if (!canvasId) return null

        const actions = getActions(String(canvasId))
        if (!actions) return null

        const filenamePrefix = `${pos?.configurazione?.posizione?.nome || 'posizione'}`
        const imageDataUrl = await actions.getPreviewImage()
        if (!imageDataUrl) return null

        const imageBlob = dataURLtoBlob(imageDataUrl)
        const canvasJson = actions.saveCanvasJson()
        const canvasJsonFile = new File(
          [JSON.stringify(canvasJson || {})],
          `bozza_${filenamePrefix}.json`,
          { type: 'application/json' }
        )

        const updateEditorStateFormData = new FormData()
        updateEditorStateFormData.append('output_image', imageBlob, `bozza_${filenamePrefix}.png`)
        updateEditorStateFormData.append('canvas_json', canvasJsonFile)
        updateEditorStateFormData.append(
          'colore_id',
          String(editorState?.articoloPrincipale?.colore?.id || '')
        )

        const logoIds =
          canvases?.[canvasId]?.data
            ?.filter((oc: any) => oc.etype === 'image')
            .map((im: any) => im.id) || []
        if (logoIds.length > 0) {
          logoIds.forEach((logoId) => {
            updateEditorStateFormData.append('logo_ids', String(logoId))
          })
        }

        await api.post(
          `editor-bozze/posizione-personalizzazione-ordine/${canvasId}/update-editor-state/`,
          updateEditorStateFormData
        )

        // L'endpoint dell'editor salva gia' larghezza e altezza come ingombro del canvas e i pantoni,
        // esattamente come nel flusso carrello. Qui aggiorniamo solo i metadati dell'ordine.
        const updatePosizioneFormData = new FormData()
        updatePosizioneFormData.append('note', note || '')
        updatePosizioneFormData.append(
          'fileBozzaDigitaleImage',
          imageBlob,
          `bozza_${filenamePrefix}.png`
        )

        return api.put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${canvasId}/`, updatePosizioneFormData, {
          params: {
            ...(hashParam ? { hash: hashParam } : {}),
          },
        })
      }) || []

    await Promise.all(requests as Promise<any>[])

    editorState?.posizioni?.forEach((pos: any) => {
      const canvasId = pos?.id
      if (!canvasId) return
      const actions = getActions(String(canvasId))
      if (!actions) return
      actions.resetDirty?.(canvases?.[canvasId]?.data || [])
    })
  }

  const resolvedNoteFromDb = useMemo(() => {
    if (selectedPosizioneOrdineId) {
      return currentPosizioneOrdine?.note || ''
    }

    return initialNote || ''
  }, [currentPosizioneOrdine, initialNote, selectedPosizioneOrdineId])

  useEffect(() => {
    if ((note || '') !== (savedNote || '')) return
    setNote(resolvedNoteFromDb)
    setSavedNote(resolvedNoteFromDb)
  }, [resolvedNoteFromDb, note, savedNote])

  const editorSource = useMemo(() => {
    return Array.isArray(router.query?.from) ? router.query.from[0] : router.query?.from
  }, [router.query?.from])

  const isCurrentPosizioneEditable = useMemo(() => {
    if (!currentPosizioneOrdine) return false

    if (editorSource === 'caricamento-bozze-digitali-ordine') {
      return getIsCaricamentoBozzePosizioneEditable(currentPosizioneOrdine, userType)
    }

    if (editorSource === 'bozze-digitali-ordine') {
      return getIsApprovazioneBozzeEditable(currentPosizioneOrdine, userType)
    }

    return false
  }, [currentPosizioneOrdine, editorSource, userType])

  const nextPosizioneData = useMemo(() => {
    const personalizzazioni = currentOrdineData?.personalizzazioniOrdine || []
    const ordini = ordineData?.ordini?.length
      ? ordineData.ordini
      : currentOrdineData
      ? [currentOrdineData]
      : []

    if (editorSource === 'caricamento-bozze-digitali-ordine') {
      // Una posizione appena salvata passa a BOZZA_SALVATA: non deve essere selezionata di nuovo.
      return getNextPosizioneCaricamentoBozzeEditabile(
        ordini,
        currentPosizioneOrdine,
        userType,
        false,
        true
      )
    }

    if (editorSource === 'bozze-digitali-ordine') {
      // non usato ancora
      return getNextPosizioneApprovazioneBozzeEditabile(
        personalizzazioni,
        currentPosizioneOrdine,
        userType
      )
    }

    return null
  }, [currentOrdineData, currentPosizioneOrdine, editorSource, ordineData, userType])

  const ordineLinguaUtente = ordineData?.linguaUtente || null
  const pantoniInfo = useMemo(() => {
    const numeroColoriLabel = currentPosizioneOrdine?.numeroColoriPosizione?.fullColor
      ? currentPosizioneOrdine?.numeroColoriPosizione?.nome
      : currentPosizioneOrdine?.numeroColoriPosizione?.numeroColori
    const pantoni = (currentPosizioneOrdine?.pantoni || [])
      .map((pantone: any) => pantone?.pantone)
      .filter(Boolean)

    return pantoni?.length > 0 ? Array.from(new Set(pantoni)).join(', ') : numeroColoriLabel || ''
  }, [currentPosizioneOrdine])

  const save = async (goNext = false) => {
    setLoading(true)
    try {
      await persistPosizioni()
      setSavedNote(note || '')

      const shouldRedirectToCaricamentoBozze = editorSource === 'caricamento-bozze-digitali-ordine'

      if (!goNext) {
        if (shouldRedirectToCaricamentoBozze && ordineNumeroProtocollo) {
          const targetUrl = `${ordineData.caricamentoBozzeDigitaliUrl}#posizione_${selectedPosizioneOrdineId}`
          router.push(targetUrl)
          return
        }
        displayNotification('editor_ordine_save_success', t('Personalizzazione salvata'), 'success')
        return
      }

      if (!nextPosizioneData) {
        if (shouldRedirectToCaricamentoBozze && ordineNumeroProtocollo) {
          const targetUrl = `${ordineData.caricamentoBozzeDigitaliUrl}#posizione_${selectedPosizioneOrdineId}`
          router.push(targetUrl)
          return
        }
        displayNotification(
          'editor_ordine_save_success_no_next',
          t('Personalizzazione salvata. Nessuna prossima personalizzazione disponibile.'),
          'success'
        )
        return
      }

      const nextUrl = getEditorUrl({
        personalizzazioneId: nextPosizioneData.personalizzazioneId,
        editorOwnerId: ordineId,
        source: editorSource,
        scope: EDITOR_SCOPE_ORDER,
        positionId: nextPosizioneData.id,
        hash: hashParam,
      })
      if (!nextUrl) return

      window.location.href = nextUrl
    } catch (err) {
      displayNotification(
        'editor_ordine_save_error',
        t('Errore durante il salvataggio della personalizzazione'),
        'error'
      )
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.note}>
        <div className={cn(styles.section, styles['section--notaCaricamento'])}>
          <p className={styles.pantoniInfo}>
            {t('Colori')}: <strong>{pantoniInfo || '-'}</strong>
          </p>
          <p className={styles.sectionTitle}>{t('Nota caricamento')}</p>
          <FormField className={styles.notaCaricamentoField}>
            <FormTextarea
              id="editor_ordine_note"
              name="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormField>
          {!!ordineLinguaUtente?.nome && (
            <div className={styles.noteFooter}>
              {t('Lingua del cliente')}: <span>{ordineLinguaUtente.nome}</span>
              <Flag locale={ordineLinguaUtente.isoCode} />
              <span>{t('(la traduzione è automatica)')}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.ctaBox}>
        {!!editorAlert && (
          <Alert
            variant={editorAlert.variant}
            placeholder={editorAlert.message}
            styleVariant="background"
          />
        )}
        {isCurrentPosizioneEditable && (
          <>
            <Button
              variant="primary"
              size="medium"
              label={t('Salva')}
              onClick={() => void save(false)}
              loading={isLoading}
              disabled={isLoading || !isValid || !isDirty}
            />
            {!!nextPosizioneData && (
              <Button
                variant="primary"
                size="medium"
                label={t('Salva e vai alla prossima')}
                icon={<ArrowRight />}
                onClick={() => void save(true)}
                loading={isLoading}
                disabled={isLoading || !isValid || !isDirty}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default EditorOrdineAzioni
